/**
 * <ReplyItem />
 *
 * Renders ONE reply from a thread. Two visual modes:
 *   * Plain reply   -- compact post with avatar circle + body.
 *   * Product reply -- boxed card with price headline, part number, DEALER
 *                      badge if the seller is a dealer, and a Buy Now button
 *                      that routes the user into the order flow.
 *
 * The backend already sorts product replies by lowest-price-first with
 * dealer tiebreak; we only need to render each entry faithfully.
 */
import * as React from "react";
import { Link } from "react-router-dom";
import { PackageCheck, Tag } from "lucide-react";
import type { Product, Reply } from "../../lib/types";
import { Badge } from "../ui/Badge";
import { Button } from "../ui/Button";
import { cn } from "../../lib/cn";

interface Props {
  reply: Reply;
  product: Product | null;
  /** Index (1-based) used for sort-rank hints on product replies. */
  rank?: number;
}

export function ReplyItem({ reply, product, rank }: Props) {
  if (product) {
    return <ProductReply reply={reply} product={product} rank={rank} />;
  }
  return <PlainReply reply={reply} />;
}

function PlainReply({ reply }: { reply: Reply }) {
  return (
    <article
      data-testid={`reply-plain-${reply.id}`}
      className="rounded-2xl border border-surface-border bg-white p-5"
    >
      <header className="flex items-center gap-3 mb-3">
        <div className="h-9 w-9 rounded-full bg-ink text-white grid place-items-center font-bold text-sm">
          {reply.author_id.slice(0, 1).toUpperCase()}
        </div>
        <div className="text-sm">
          <div className="font-semibold text-ink">Community member</div>
          <div className="text-ink-muted">
            {new Date(reply.created_at).toLocaleString()}
          </div>
        </div>
        {reply.is_accepted_answer && (
          <Badge variant="soft" className="ml-auto">Accepted answer</Badge>
        )}
      </header>
      <p className="text-ink whitespace-pre-line leading-relaxed">{reply.body}</p>
    </article>
  );
}

function ProductReply({
  reply,
  product,
  rank,
}: {
  reply: Reply;
  product: Product;
  rank?: number;
}) {
  const freeShipping = product.shipping_type === "FREE";
  return (
    <article
      data-testid={`reply-product-${reply.id}`}
      className={cn(
        "rounded-2xl border-2 border-ink bg-white p-5 shadow-card",
        "relative overflow-hidden",
      )}
    >
      {rank && (
        <span
          aria-hidden
          className="absolute top-0 right-0 bg-ink text-white text-xs font-black uppercase tracking-wider px-3 py-1.5 rounded-bl-2xl"
        >
          Match #{rank}
        </span>
      )}

      <header className="flex items-center gap-3 mb-3">
        <div className="h-9 w-9 rounded-full bg-brand text-white grid place-items-center font-bold text-sm">
          {(product.seller_username ?? "?").slice(0, 1).toUpperCase()}
        </div>
        <div className="text-sm">
          <div className="font-semibold text-ink">
            @{product.seller_username ?? "seller"}
          </div>
          <div className="text-ink-muted">
            {new Date(reply.created_at).toLocaleString()}
          </div>
        </div>
      </header>

      <p className="text-ink leading-relaxed mb-4">{reply.body}</p>

      <div className="rounded-xl border border-surface-border bg-surface-raised p-4">
        <div className="flex flex-wrap items-start justify-between gap-3 mb-2">
          <div>
            <h4 className="text-lg font-bold text-ink leading-tight">
              {product.title}
            </h4>
            <div className="flex items-center gap-2 text-sm text-ink-muted mt-1">
              <Tag className="h-4 w-4" aria-hidden />
              <span className="font-mono">{product.part_number}</span>
              <span>·</span>
              <span>{product.condition}</span>
              <span>·</span>
              <span>{product.stock_quantity} in stock</span>
            </div>
          </div>
          <div className="text-right">
            <div className="font-display text-3xl font-black text-ink leading-none">
              ${product.price.toFixed(2)}
            </div>
            <div className="text-xs text-ink-muted mt-1">
              {freeShipping ? "Free shipping" : `+ $${product.shipping_cost.toFixed(2)} shipping`}
            </div>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2 mb-4">
          <Badge variant="brand" size="sm">Dealer</Badge>
          {product.featured_until &&
            new Date(product.featured_until) > new Date() && (
              <Badge variant="soft" size="sm">Featured</Badge>
            )}
          {freeShipping && (
            <Badge variant="outline" size="sm">
              <PackageCheck className="h-3 w-3 mr-1" aria-hidden /> Free ship
            </Badge>
          )}
        </div>

        <Link to={`/product/${product.id}`} data-testid={`buy-now-${product.id}`}>
          <Button variant="primary" size="md" fullWidth>
            Buy now — ${product.price.toFixed(2)}
          </Button>
        </Link>
      </div>
    </article>
  );
}
